// Storage helpers that never throw — localStorage/sessionStorage can be absent
// or blocked (private mode, embedded webviews, quota). On failure we degrade to
// in-memory maps so a single launch still behaves consistently.

const memoryLocal = new Map<string, string>();
const memorySession = new Map<string, string>();

const isBrowser = () => typeof window !== "undefined";

const safeStore = (kind: "local" | "session"): Storage | null => {
  if (!isBrowser()) return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

export const local = {
  get(key: string): string | null {
    const store = safeStore("local");
    if (!store) return memoryLocal.get(key) ?? null;
    try {
      return store.getItem(key);
    } catch {
      return memoryLocal.get(key) ?? null;
    }
  },
  set(key: string, value: string) {
    memoryLocal.set(key, value);
    const store = safeStore("local");
    try {
      store?.setItem(key, value);
    } catch {
      /* keep the in-memory copy */
    }
  },
};

export const session = {
  get(key: string): string | null {
    const store = safeStore("session");
    if (!store) return memorySession.get(key) ?? null;
    try {
      return store.getItem(key);
    } catch {
      return memorySession.get(key) ?? null;
    }
  },
  set(key: string, value: string) {
    memorySession.set(key, value);
    const store = safeStore("session");
    try {
      store?.setItem(key, value);
    } catch {
      /* keep the in-memory copy */
    }
  },
  remove(key: string) {
    memorySession.delete(key);
    const store = safeStore("session");
    try {
      store?.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
